import React from "react";
import { Box, FormLabel } from "@mui/joy";
import type { Training } from "../../../../api/training/Training.typed";
import { TrainingStatusCode } from "../../../../api/training/Training.typed";
import type { ZekayCustomer } from "../../../../api/customers/Customer.typed";
import { normalizeMultiCommentList } from "../../../../api/zekay/comments";
import type { MultiComment } from "../../../../api/zekay/comments";
import { useCustomer, useFollowup, useTraining } from "../../../../contexts/api";
import { useAlert } from "../../../../contexts/app/notificationToast";
import { useAuth } from "../../../../contexts/api";
import useAppSettings from "../../../../contexts/app/appSettings";
import { userDisplayName } from "../../../../utils/userHelper";
import { usePermissions } from "../../../../contexts/app/permissions";
import { Permission } from "../../../../api/zekay/permissions";
import { areAllDatesValid } from "../../../common/DatesComponent";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import DetailsTooltip from "../../../common/DetailsTooltip";
import CommentsModal from "../../../common/CommentsModal";
import DatesComponent from "../../../common/DatesComponent";
import DocumentsComponent, { getDocumentsBadgeStatus } from "../../../common/DocumentsComponent";
import TheoryTestPanel from "../../../shared/theoryTest/TheoryTestPanel";
import {
  hasTheoryTestHistory,
  isTheoryTestValidated,
} from "../../../shared/theoryTest/theoryTestHelpers";
import DarkInput from "../../../common/DarkInput";
import DarkTypography from "../../../common/DarkTypography";
import {
  trainingActionSlotSpacerSx,
  trainingCardActionSlotSx,
  trainingMetaActionsSlotSx,
} from "../../common/trainingCardActionsSx";
import type { FollowUpSheet } from "../../../../api/followup/Followup.typed";
import FollowUpSheetAction from "../../../shared/followup/FollowUpSheetAction";
import { SDate, SFile, SComment, SQuiz, SSendMail } from "../../../../styles/StyledMUI";
import { extractDataFromResponse } from "../../../../utils/paginationHelpers";
import TrainingItemPanels from "./TrainingItemPanels";
import TrainingIdentityBlock from "../../common/TrainingIdentityBlock";
import TrainingListRowGuard from "../../common/TrainingListRowGuard";
import TrainingRowTooltipButton from "../../common/TrainingRowTooltipButton";
import { useTrainingCustomerProfile } from "../../common/hooks/useTrainingCustomerProfile";
import { useTrainingMail } from "../../common/hooks/useTrainingMail";
import TrainingStatusActionSelect from "../../common/TrainingStatusActionSelect";
import type { TrainingStatusAction } from "../../common/trainingStatusActionTokens";
import { trainingStatusMenuActions } from "../../common/trainingStatusMenuActions";
import { useConfirmationModal } from "../../../../contexts/app/confirmation";
import ListRowAlertBanner from "../../../common/ListRowAlertBanner";
import { trainingAlertLabels } from "../../common/trainingAlerts";
import {
  colorTokens,
  gradientListRowCardShellSx,
  listRowInactivityAlertSx,
  trainingItemActionsClusterSx,
  trainingItemIdentityColumnSx,
  trainingItemRowSx,
  trainingItemSessionsWrapperSx,
  trainingRowHighlightBgSx,
} from "../../../../styles/globalStyles";
import type { SxProps } from "@mui/joy/styles/types";

interface TrainingItemProps {
  training: Training;
  initialCustomer?: ZekayCustomer;
  setTrainings: React.Dispatch<React.SetStateAction<Training[] | undefined>>;
  updateTrainingLocally?: (training: Training) => void;
  removeTrainingLocally?: (customerID: number) => void;
  refreshTrainings?: () => Promise<Training[] | undefined>;
  status: number;
  isOpenDocuments?: boolean;
  onToggleDocuments?: () => void;
  highlighted?: boolean;
}

const TrainingItem: React.FC<TrainingItemProps> = React.memo(
  ({
    training,
    initialCustomer,
    setTrainings,
    updateTrainingLocally,
    removeTrainingLocally,
    refreshTrainings: refreshTrainingsFromParent,
    status,
    isOpenDocuments: isOpenDocumentsProp,
    onToggleDocuments,
    highlighted,
  }) => {
    const { getCustomer } = useCustomer();
    const { sendTrainingInProgressMail } = useTrainingMail();
    const {
      cancelTraining,
      updateTraining,
      getTrainingsByTemplateIdAndStatus,
      isTheoryTestOnline,
    } = useTraining();
    const { createFollowUpSheet } = useFollowup();
    const { user } = useAuth();
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(Permission.TRAININGS_EDIT);
    const canEditProfile = hasPermission(Permission.CUSTOMERS_PROFILE_EDIT);
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmationModal();
    const { config } = useAppSettings();
    const isDesync = config?.CustomersSync === false;
    const trainingStatusMenuActionList = React.useMemo((): TrainingStatusAction[] => {
      if (status === TrainingStatusCode.Cancelled) {
        return ["en_cours"];
      }
      return trainingStatusMenuActions(config?.CustomersSync === true, "inProgress");
    }, [config?.CustomersSync, status]);

    const { customer, setCustomer, profileFormOpen, setProfileFormOpen, openProfile } =
      useTrainingCustomerProfile({
        training,
        initialCustomer,
        getCustomer,
        showAlert,
      });
    const [multiComments, setMultiComments] = React.useState<MultiComment[] | undefined>(() =>
      normalizeMultiCommentList(training.MultiComments as unknown[] | undefined),
    );
    const [testResult, setTestResult] = React.useState(training.TestResult || "");
    const [theory, setTheory] = React.useState<Dayjs | null>(
      training.Theory && training.Theory.trim() !== "" ? dayjs(training.Theory) : null,
    );
    const [rifap, setRifap] = React.useState<Dayjs | null>(
      training.RifapDate && training.RifapDate.trim() !== "" ? dayjs(training.RifapDate) : null,
    );
    const [commentsModalOpen, setCommentsModalOpen] = React.useState(false);
    const [sessionsValue, setSessionsValue] = React.useState<string>(
      String(training.Sessions ?? 0),
    );
    const [isOpenDocumentsLocal, setIsOpenDocumentsLocal] = React.useState(false);
    const isOpenDocuments =
      onToggleDocuments != null ? (isOpenDocumentsProp ?? false) : isOpenDocumentsLocal;
    const handleToggleDocuments =
      onToggleDocuments ?? (() => setIsOpenDocumentsLocal((prev) => !prev));
    const [isOpenDates, setIsOpenDates] = React.useState(false);
    const [isOpenTheoryTests, setIsOpenTheoryTests] = React.useState(false);

    React.useEffect(() => {
      setMultiComments(normalizeMultiCommentList(training.MultiComments as unknown[] | undefined));
    }, [training.MultiComments]);

    React.useEffect(() => {
      setSessionsValue(String(training.Sessions ?? 0));
    }, [training.Sessions]);

    const sessionsEditable = canEdit && isDesync;

    const commitSessions = React.useCallback(async () => {
      const parsed = parseInt(sessionsValue.trim(), 10);
      const next = Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;

      if (next !== parsed) {
        setSessionsValue(String(next));
      }

      if (next === (training.Sessions ?? 0)) return;

      const updatedTraining: Training = {
        ...training,
        Sessions: next,
        UpdatedBy: userDisplayName(user),
        UpdatedAt: new Date(),
      };

      try {
        await updateTraining(updatedTraining);
        updateTrainingLocally?.(updatedTraining);
      } catch (error) {
        setSessionsValue(String(training.Sessions ?? 0));
        showAlert("Erreur lors de la mise à jour du nombre de séances", "danger");
      }
    }, [sessionsValue, training, user, updateTraining, updateTrainingLocally, showAlert]);

    const showTheoryTest =
      isTheoryTestOnline(training.TrainingTemplateID) ||
      hasTheoryTestHistory(testResult, training.TheoryTests);

    const hasCustomerEmail = (customer?.MailAddress ?? "").trim() !== "";

    const handleCommentsSave = React.useCallback(
      async (nextMulti?: MultiComment[]) => {
        const { UpdatedAt: _updatedAt, ...trainingPayload } = training;
        const updatedTraining = {
          ...trainingPayload,
          MultiComments: nextMulti ?? multiComments,
          TestResult: testResult,
          Theory: theory ? theory.toISOString() : "",
          RifapDate: rifap ? rifap.toISOString() : "",
          UpdatedBy: userDisplayName(user),
        };

        try {
          await updateTraining(updatedTraining);
          setMultiComments(
            normalizeMultiCommentList(updatedTraining.MultiComments as unknown[] | undefined),
          );
        } catch (error) {
          showAlert("Erreur lors de la mise à jour des commentaires", "danger");
        }
      },
      [training, testResult, theory, rifap, user, multiComments, updateTraining, showAlert],
    );

    const handleSendMail = React.useCallback(
      () => sendTrainingInProgressMail(training),
      [sendTrainingInProgressMail, training],
    );

    const syncListAfterStatusChange = React.useCallback(async () => {
      if (removeTrainingLocally && training.CustomerID) {
        removeTrainingLocally(training.CustomerID);
      } else {
        const result = await getTrainingsByTemplateIdAndStatus(
          String(training.TrainingTemplateID),
          status,
        );
        const trainingsData = extractDataFromResponse(result) as Training[];
        setTrainings(trainingsData);
      }
    }, [training, removeTrainingLocally, getTrainingsByTemplateIdAndStatus, status, setTrainings]);

    const handleStatusAction = React.useCallback(
      async (action: TrainingStatusAction) => {
        if (action === "valider") {
          return;
        }
        if (action === "en_cours") {
          const ok = await showConfirmation(
            "Remettre en cours",
            "Repasser la formation en « En cours » ?",
          );
          if (!ok) return;
          try {
            await updateTraining({
              ...training,
              Status: "En cours",
              UpdatedBy: userDisplayName(user),
              UpdatedAt: new Date(),
            });
            await syncListAfterStatusChange();
          } catch (error) {
            showAlert("Erreur lors de la mise à jour du statut", "danger");
          }
          return;
        }
        if (action === "annuler") {
          const ok = await showConfirmation(
            "Annuler la formation",
            "Confirmer l'annulation de cette formation ?",
          );
          if (!ok) return;
          try {
            await cancelTraining(training, { skipConfirmation: true });
            await syncListAfterStatusChange();
          } catch (error) {
            showAlert("Erreur lors de l'annulation de la formation", "danger");
          }
          return;
        }
        if (action === "terminer") {
          const ok = await showConfirmation(
            "Terminer la formation",
            "Passer cette formation en « Terminé » ?",
          );
          if (!ok) return;
          try {
            await updateTraining({
              ...training,
              Status: "Terminé",
              UpdatedBy: userDisplayName(user),
              UpdatedAt: new Date(),
            });
            await syncListAfterStatusChange();
          } catch (error) {
            showAlert("Erreur lors de la mise à jour du statut", "danger");
          }
        }
      },
      [
        training,
        cancelTraining,
        syncListAfterStatusChange,
        showAlert,
        showConfirmation,
        updateTraining,
        user,
      ],
    );

    const getTrainingsByTemplateIdWrapper = React.useCallback(
      async (templateId: string): Promise<Training[] | undefined> => {
        const result = await getTrainingsByTemplateIdAndStatus(templateId, status);
        return extractDataFromResponse(result) as Training[];
      },
      [getTrainingsByTemplateIdAndStatus, status],
    );

    const refreshTrainingsWrapper = React.useCallback(async (): Promise<Training[] | undefined> => {
      if (refreshTrainingsFromParent) return refreshTrainingsFromParent();
      const result = await getTrainingsByTemplateIdAndStatus(
        String(training.TrainingTemplateID),
        status,
      );
      const data = extractDataFromResponse(result) as Training[];
      setTrainings(data);
      return data;
    }, [
      refreshTrainingsFromParent,
      getTrainingsByTemplateIdAndStatus,
      training.TrainingTemplateID,
      status,
      setTrainings,
    ]);

    const handleCommentsModalClose = React.useCallback(() => {
      setCommentsModalOpen(false);
      if (updateTrainingLocally) {
        updateTrainingLocally({
          ...training,
          MultiComments: multiComments,
          TestResult: testResult,
          Theory: theory ? theory.toISOString() : "",
          RifapDate: rifap ? rifap.toISOString() : "",
        });
      } else {
        void refreshTrainingsWrapper();
      }
    }, [
      training,
      multiComments,
      testResult,
      theory,
      rifap,
      updateTrainingLocally,
      refreshTrainingsWrapper,
    ]);

    const alertLabels = trainingAlertLabels(training);
    const hasAlert = alertLabels.length > 0;

    const placeholder = (
      <DarkTypography level="h4" colorVariant="muted">
        —
      </DarkTypography>
    );
    const showMail = canEdit && status === 1;
    const docsStatus = React.useMemo(
      () => getDocumentsBadgeStatus(training, customer),
      [training, customer],
    );
    const showStatusActions =
      canEdit &&
      (status === TrainingStatusCode.InProgress || status === TrainingStatusCode.Cancelled);
    const handleCreateFollowUp = React.useCallback(async (): Promise<boolean> => {
      if (!training.CustomerID || training.TrainingTemplateID == null) {
        showAlert("Données manquantes pour créer la fiche de suivi", "warning");

        return false;
      }
      const ok = await showConfirmation(
        "Créer la fiche de suivi",
        "Une nouvelle fiche de suivi sera initialisée pour ce client.",
      );
      if (!ok) return false;

      try {
        await createFollowUpSheet({
          CustomerId: training.CustomerID,
          TrainingTemplateID: training.TrainingTemplateID,
          Logs: [],
        });
        await refreshTrainingsWrapper();

        return true;
      } catch (error) {
        showAlert("Erreur lors de la création de la fiche de suivi", "danger");

        return false;
      }
    }, [
      training.CustomerID,
      training.TrainingTemplateID,
      createFollowUpSheet,
      refreshTrainingsWrapper,
      showAlert,
      showConfirmation,
    ]);

    const handleFollowUpSaved = React.useCallback(
      (sheet: FollowUpSheet) => {
        if (updateTrainingLocally) {
          updateTrainingLocally({ ...training, FollowUpSheet: sheet });
        } else {
          void refreshTrainingsWrapper();
        }
      },
      [training, updateTrainingLocally, refreshTrainingsWrapper],
    );

    const shellSx: SxProps = hasAlert
      ? ([gradientListRowCardShellSx, listRowInactivityAlertSx] as unknown as SxProps)
      : highlighted
        ? ([gradientListRowCardShellSx, trainingRowHighlightBgSx] as unknown as SxProps)
        : gradientListRowCardShellSx;

    return (
      <>
        <Box sx={shellSx}>
          <Box sx={{ p: 1.5 }}>
            <ListRowAlertBanner labels={alertLabels} />
            <Box sx={trainingItemRowSx}>
              <Box sx={trainingItemIdentityColumnSx}>
                <TrainingIdentityBlock
                  training={training}
                  canOpenProfile={canEdit}
                  onOpenProfile={openProfile}
                  fallback={placeholder}
                />

                {isDesync ? (
                  <Box sx={trainingItemSessionsWrapperSx}>
                    <FormLabel
                      htmlFor={`training-sessions-${training.ID}`}
                      sx={{ fontSize: 10, lineHeight: 1, m: 0, color: colorTokens.text.dimmed }}
                    >
                      Séances
                    </FormLabel>
                    <DarkInput
                      id={`training-sessions-${training.ID}`}
                      type="number"
                      size="sm"
                      aria-label="Nombre de séances"
                      value={sessionsValue}
                      disabled={!sessionsEditable}
                      onChange={(e) => setSessionsValue(e.target.value)}
                      onBlur={() => void commitSessions()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      slotProps={{
                        input: {
                          type: "number",
                          min: 0,
                          max: 999,
                          step: 1,
                          inputMode: "numeric",
                        },
                      }}
                      sx={{ width: 64, minHeight: 28 }}
                    />
                  </Box>
                ) : null}
              </Box>

              <Box sx={trainingItemActionsClusterSx}>
                <Box sx={trainingCardActionSlotSx}>
                  <TrainingListRowGuard enabled={canEdit} fallback={placeholder}>
                    <TrainingRowTooltipButton
                      title="Commentaires"
                      ariaLabel="Commentaires"
                      icon={<SComment />}
                      onClick={() => setCommentsModalOpen(true)}
                      ctaVariant="accent"
                      badgeContent={multiComments?.length ?? 0}
                      badgeColor="neutral"
                    />
                  </TrainingListRowGuard>
                </Box>
                <Box sx={trainingCardActionSlotSx}>
                  {canEdit && (training.NeedsTheory || training.NeedsRifap) ? (
                    <TrainingRowTooltipButton
                      title="Sessions théoriques"
                      ariaLabel="Sessions théoriques"
                      icon={<SDate />}
                      onClick={() => setIsOpenDates(!isOpenDates)}
                      ctaVariant="accent"
                      badgeContent={areAllDatesValid(training, theory, rifap) ? "✓" : 0}
                      badgeColor="success"
                    />
                  ) : !canEdit ? (
                    placeholder
                  ) : (
                    <Box sx={trainingActionSlotSpacerSx} />
                  )}
                </Box>
                <Box sx={trainingCardActionSlotSx}>
                  <TrainingListRowGuard enabled={canEdit} fallback={placeholder}>
                    <TrainingRowTooltipButton
                      title="Docs"
                      ariaLabel="Documents"
                      icon={<SFile />}
                      onClick={handleToggleDocuments}
                      ctaVariant="accent"
                      badgeContent={
                        docsStatus.allUploaded ? "✓" : `${docsStatus.uploaded}/${docsStatus.total}`
                      }
                      badgeColor={docsStatus.allUploaded ? "success" : "neutral"}
                    />
                  </TrainingListRowGuard>
                </Box>
                <Box sx={trainingCardActionSlotSx}>
                  {canEdit && showTheoryTest ? (
                    <TrainingRowTooltipButton
                      title="QCM"
                      ariaLabel="QCM"
                      icon={<SQuiz />}
                      onClick={() => setIsOpenTheoryTests(!isOpenTheoryTests)}
                      ctaVariant="accent"
                      badgeContent={
                        isTheoryTestValidated(testResult, training.TheoryTests) ? "✓" : 0
                      }
                      badgeColor="success"
                    />
                  ) : !canEdit ? (
                    placeholder
                  ) : (
                    <Box sx={trainingActionSlotSpacerSx} />
                  )}
                </Box>
                <Box sx={trainingCardActionSlotSx}>
                  <FollowUpSheetAction
                    training={training}
                    status={status}
                    customerName={[training.FirstName, training.LastName].filter(Boolean).join(" ")}
                    onSaved={handleFollowUpSaved}
                    onCreate={handleCreateFollowUp}
                    fallback={placeholder}
                  />
                </Box>
                <Box sx={trainingCardActionSlotSx}>
                  {showMail ? (
                    <TrainingRowTooltipButton
                      title={hasCustomerEmail ? "Mail" : "Email client manquant"}
                      ariaLabel={hasCustomerEmail ? "Envoyer mail" : "Mail indisponible"}
                      icon={<SSendMail />}
                      onClick={hasCustomerEmail ? handleSendMail : undefined}
                      disabled={!hasCustomerEmail}
                      ctaVariant="accent"
                    />
                  ) : !canEdit ? (
                    placeholder
                  ) : (
                    <Box sx={trainingActionSlotSpacerSx} />
                  )}
                </Box>
                <Box sx={trainingCardActionSlotSx}>
                  {showStatusActions ? (
                    <TrainingStatusActionSelect
                      listKey={training.ID ?? training.CustomerID ?? 0}
                      allowedActions={trainingStatusMenuActionList}
                      onAction={handleStatusAction}
                    />
                  ) : !canEdit ? (
                    placeholder
                  ) : (
                    <Box sx={trainingActionSlotSpacerSx} />
                  )}
                </Box>
                <Box sx={trainingMetaActionsSlotSx}>
                  <DetailsTooltip training={training} />
                </Box>
              </Box>
            </Box>

            {isOpenDocuments && (
              <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
                <DocumentsComponent
                  training={training}
                  customer={customer}
                  setTrainings={setTrainings}
                  refreshTrainings={refreshTrainingsWrapper}
                  standalone
                  onCustomerUpdated={setCustomer}
                />
              </Box>
            )}
            {isOpenDates && (
              <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
                <DatesComponent
                  training={training}
                  theory={theory}
                  rifap={rifap}
                  setTheory={setTheory}
                  setRifap={setRifap}
                  updateTraining={updateTraining}
                  getTrainingsByTemplateId={getTrainingsByTemplateIdWrapper}
                  setTrainings={setTrainings}
                  isOpenDates={isOpenDates}
                  status={status}
                  standalone
                />
              </Box>
            )}
            <CommentsModal
              open={commentsModalOpen}
              title="Commentaires"
              currentUserName={userDisplayName(user)}
              multiComments={multiComments}
              setMultiComments={setMultiComments}
              onSave={handleCommentsSave}
              onClose={handleCommentsModalClose}
              allowEdit={canEdit}
            />
            {isOpenTheoryTests && showTheoryTest && (
              <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
                <TheoryTestPanel
                  training={training}
                  testResult={testResult}
                  setTestResult={setTestResult}
                  updateTraining={updateTraining}
                  getTrainingsByTemplateId={getTrainingsByTemplateIdWrapper}
                  setTrainings={setTrainings}
                  isOpenTheoryTests={isOpenTheoryTests}
                  status={status}
                  standalone
                />
              </Box>
            )}
          </Box>
        </Box>

        <TrainingItemPanels
          training={training}
          setTrainings={setTrainings}
          refreshTrainingsFromParent={refreshTrainingsFromParent}
          profileFormOpen={profileFormOpen}
          customer={customer}
          setProfileFormOpen={setProfileFormOpen}
          setCustomer={setCustomer}
          profileEditable={canEditProfile && isDesync}
          desync={isDesync}
        />
      </>
    );
  },
);

TrainingItem.displayName = "TrainingItem";

export default TrainingItem;
